import apiClient from './apiClient';
import { UserBrand, BrandUser, BrandRole } from '../types/brand';

// Request/Response Types

export interface CreateRssSourceRequest {
  feed_url: string;
  name?: string;
}

export interface FeedValidationInfo {
  title?: string;
  description?: string;
  entries_count?: number;
  sample_entry?: {
    title?: string;
    link?: string;
    published?: string;
  };
}

export interface CreateRssSourceResponse {
  success: boolean;
  source_id?: number;
  feed_url: string;
  feed_title?: string;
  articles_imported: number;
  message: string;
  validation_info?: FeedValidationInfo;
}

export interface DeleteRssSourceResponse {
  success: boolean;
  message: string;
  items_deleted?: number;
  items_kept?: number;
}

export interface RssFeed {
  id: number;
  name: string;
  url: string;
  active: boolean;
  article_count?: number;
  last_fetched?: string;
  source_method?: 'rss' | 'homepage_scraping';
  last_refresh_status?: 'success' | 'error' | 'pending';
  failure_reason?: string;
  auto_disabled?: boolean;
  consecutive_failures?: number;
  /** Adaptive back-off (P6): the feed's own cadence (advertised TTL or the
      default), the interval the scheduler actually uses right now, how many
      refreshes in a row brought nothing, when the next one is due and when
      the feed last produced an item. */
  base_interval_minutes?: number;
  refresh_interval_minutes?: number;
  idle_streak?: number;
  next_refresh_at?: string | null;
  last_new_item_at?: string | null;
}

export interface RssFeedListResponse {
  feeds: RssFeed[];
  total: number;
}

export interface ValidateFeedRequest {
  url: string;
}

export interface ValidateFeedResponse {
  valid: boolean;
  url: string;
  title?: string;
  description?: string;
  article_count?: number;
  items_by_subtype?: Record<string, number>;  // {article|short|long|podcast: count}
  sample_entry?: {
    title?: string;
    link?: string;
  };
  error?: string;
}

export interface DiscoveredRssFeed {
  url: string;
  title: string;
  type: string;
  article_count: number;
  validation?: Record<string, any>;
  /** Comprehensive discovery: main feed + section feeds the main feed misses */
  recommended?: boolean;
  feed_role?: 'main' | 'section';
  unique_count?: number;
}

export interface DiscoverRssFeedsResponse {
  url: string;
  rss_feeds: DiscoveredRssFeed[];
  count: number;
}

export interface RefreshFeedResponse {
  success: boolean;
  items_added: number;
  items_updated: number;
  message: string;
}

/** POST /sweep now kicks off a background job and returns immediately. */
export interface SweepStartResponse {
  job_id: number;
  status: string;
  feeds_requested: number;
}

/** GET /sweep/{job_id} — live progress + final counts. */
export interface SweepJobStatus {
  job_id: number;
  brand_id: number | null;
  status: 'pending' | 'running' | 'done' | 'error';
  feeds_total: number;
  feeds_done: number;
  items_added: number;
  items_updated: number;
  items_skipped: number;
  message: string | null;
  created_at: string | null;
  updated_at: string | null;
}

// RSS Auto-Refresh Types
export interface AutoRefreshToggleResponse {
  success: boolean;
  slug: string;
  auto_refresh_enabled: boolean;
  message: string;
}

export interface NewItemsCountResponse {
  slug: string;
  new_items_count: number;
  auto_refresh_enabled: boolean;
}

// Editorial Content Intelligence Types
export interface EditorialStats {
  brand_name: string;
  period_days: number;
  total_articles: number;
  hourly_breakdown: Record<string, number>;
  timestamp_granularity: { exact_second: number; rounded_minute: number };
  peak_hour: string | null;
  peak_hour_count: number;
  articles: Array<{ title: string; published_at: string | null }>;
}

export interface EditorialInsights {
  insight: string;
}

// Brand Deletion Types
export interface BrandDeletePreview {
  found: boolean;
  brand: {
    id: number;
    slug: string;
    name: string;
    domain: string;
    active: boolean;
  };
  counts: {
    content_sources: number;
    content_items: number;
    publisher_logos: number;
    tendv2_transformations: number;
    tendv2_overlay_templates: number;
    curated_content: number;
    ayrshare_publish_history: number;
    ayrshare_webhooks: number;
    brand_social_connections: number;
    brand_ayrshare_settings: number;
    publisher_users: number;
    brand_invitations: number;
  };
  total_records: number;
  directories: Array<{
    path: string;
    type: string;
    exists: boolean;
    file_count: number;
  }>;
  total_directories: number;
}

export interface BrandActionResponse {
  success: boolean;
  action: 'archived' | 'restored' | 'deleted';
  brand_name: string;
  message: string;
  deleted_counts?: Record<string, number>;
  directories_deleted?: Array<{
    path: string;
    type: string;
    status: string;
    error?: string;
  }>;
}

export const brandApi = {
  /**
   * Add new RSS feed to existing brand
   */
  async addRssFeed(brandSlug: string, feedUrl: string, name?: string): Promise<CreateRssSourceResponse> {
    return apiClient.post<CreateRssSourceResponse>(`brands/${brandSlug}/feeds`, {
      feed_url: feedUrl,
      name
    });
  },

  /**
   * Delete RSS feed source
   */
  async deleteRssFeed(brandSlug: string, sourceId: number, cascade: boolean = false): Promise<DeleteRssSourceResponse> {
    return apiClient.delete<DeleteRssSourceResponse>(
      `brands/${brandSlug}/feeds/${sourceId}?cascade=${cascade}`
    );
  },

  /**
   * Toggle RSS feed active/inactive state
   */
  async toggleRssFeed(brandSlug: string, sourceId: number): Promise<{ success: boolean; source_id: number; active: boolean; message: string }> {
    return apiClient.patch(`brands/${brandSlug}/feeds/${sourceId}/toggle`);
  },

  /**
   * List RSS feeds for a brand
   */
  async listRssFeeds(brandSlug: string): Promise<RssFeedListResponse> {
    return apiClient.get<RssFeedListResponse>(`brands/${brandSlug}/feeds`);
  },

  /**
   * Validate RSS feed URL before adding
   */
  async validateFeed(url: string): Promise<ValidateFeedResponse> {
    return apiClient.post<ValidateFeedResponse>('brands/validate-feed', { url });
  },

  /**
   * Quick RSS feed discovery for a site (HTML link tags + common paths),
   * through the anti-blocking fetch ladder — can take 10-60s on
   * fingerprint-walled sites.
   */
  async discoverRssFeeds(url: string): Promise<DiscoverRssFeedsResponse> {
    return apiClient.post<DiscoverRssFeedsResponse>('brands/discover-rss', { url });
  },

  /**
   * Kick off a one-time "Recent Sweep": pull recent items from a set of section
   * feeds into a brand's library without subscribing permanently. Returns
   * immediately with a job id — the sweep runs in the background (a large sweep
   * outlasts request timeouts). Poll getSweepStatus for progress + final count.
   * Items file under the brand's "Discovered" sink; cross-source dedup means
   * only content the brand's existing feeds miss is added.
   */
  async sweepFeeds(brandSlug: string, feedUrls: string[]): Promise<SweepStartResponse> {
    return apiClient.post<SweepStartResponse>(`brands/${brandSlug}/sweep`, { feed_urls: feedUrls });
  },

  /** Refresh every active feed of a brand as ONE background job (P6.1);
      poll getSweepStatus with the returned job_id. */
  async refreshAllFeeds(brandSlug: string): Promise<SweepStartResponse> {
    return apiClient.post<SweepStartResponse>(`brands/${brandSlug}/feeds/refresh-all`, {});
  },

  /** Poll a Recent Sweep's (or Refresh All's) live progress / final counts. */
  async getSweepStatus(brandSlug: string, jobId: number): Promise<SweepJobStatus> {
    return apiClient.get<SweepJobStatus>(`brands/${brandSlug}/sweep/${jobId}`);
  },

  /**
   * Refresh RSS feed to fetch new articles
   * @param sourceId - The source ID to refresh
   */
  async refreshFeed(sourceId: number): Promise<RefreshFeedResponse> {
    return apiClient.post<RefreshFeedResponse>(`content/sources/${sourceId}/refresh`, {});
  },

  // User Management Functions

  /**
   * Get brands for the current authenticated user
   */
  async getUserBrands(): Promise<UserBrand[]> {
    return apiClient.get<UserBrand[]>('brands/');
  },

  /**
   * Get brands for a specific user by ID
   */
  async getUserBrandsById(userId: string): Promise<UserBrand[]> {
    return apiClient.get<UserBrand[]>(`brands/users/${userId}/brands`);
  },

  /**
   * Get all users in the system
   */
  async getAllUsers(): Promise<any[]> {
    return apiClient.get<any[]>('brands/users');
  },

  /**
   * Get brand details by slug
   */
  async getBrand(brandSlug: string): Promise<any> {
    const response = await apiClient.get<{ brand: any }>(`brands/${brandSlug}`);
    return response.brand;
  },

  /**
   * Get all users associated with a brand
   */
  async getBrandUsers(brandId: string): Promise<BrandUser[]> {
    return apiClient.get<BrandUser[]>(`brands/${brandId}/users`);
  },

  /**
   * Add a user to a brand
   */
  async addUserToBrand(
    brandId: string,
    email: string,
    role: BrandRole
  ): Promise<void> {
    await apiClient.post(`brands/${brandId}/users`, {
      email,
      role
    });
  },

  /**
   * Remove a user from a brand
   */
  async removeUserFromBrand(
    brandId: string,
    userId: string
  ): Promise<void> {
    await apiClient.delete(`brands/${brandId}/users/${userId}`);
  },

  /**
   * Update a user's role in a brand
   */
  async updateUserRole(
    brandId: string,
    userId: string,
    role: BrandRole
  ): Promise<void> {
    await apiClient.put(`brands/${brandId}/users/${userId}`, {
      role
    });
  },

  /**
   * Accept a brand invitation
   */
  async acceptInvitation(token: string): Promise<any> {
    return apiClient.post<any>('brands/invitations/accept', {
      token
    });
  },

  // RSS Auto-Refresh Functions

  /**
   * Toggle auto-refresh for a brand's RSS feeds
   */
  async toggleAutoRefresh(brandSlug: string, enabled: boolean): Promise<AutoRefreshToggleResponse> {
    return apiClient.patch<AutoRefreshToggleResponse>(
      `brands/${brandSlug}/auto-refresh?enabled=${enabled}`
    );
  },

  /**
   * Get auto-refresh state for a brand (endpoint also returns the legacy
   * hidden-item count; unused since the reveal queue was retired)
   */
  async getNewItemsCount(brandSlug: string): Promise<NewItemsCountResponse> {
    return apiClient.get<NewItemsCountResponse>(`brands/${brandSlug}/new-items-count`);
  },

  // Editorial Content Intelligence Functions

  /**
   * Get editorial content publishing statistics for a brand
   */
  async getEditorialStats(brandSlug: string, days?: number): Promise<EditorialStats> {
    const params = days ? { days } : undefined;
    return apiClient.get<EditorialStats>(`brands/${brandSlug}/editorial-stats`, params);
  },

  /**
   * Get LLM-generated editorial insights for a brand
   */
  async getEditorialInsights(brandSlug: string, days?: number): Promise<EditorialInsights> {
    const params = days ? { days } : undefined;
    return apiClient.get<EditorialInsights>(`brands/${brandSlug}/editorial-insights`, params);
  },

  // Brand Deletion/Archive Functions

  /**
   * Get preview of what will be deleted for a brand
   * SuperAdmin only
   */
  async getDeletePreview(brandSlug: string): Promise<BrandDeletePreview> {
    return apiClient.get<BrandDeletePreview>(`brands/${brandSlug}/delete-preview`);
  },

  /**
   * Archive (soft delete) a brand
   * Sets brand.active = false, preserves all data
   */
  async archiveBrand(brandSlug: string): Promise<BrandActionResponse> {
    return apiClient.post<BrandActionResponse>(`brands/${brandSlug}/archive`, {});
  },

  /**
   * Restore an archived brand
   * SuperAdmin only
   */
  async restoreBrand(brandSlug: string): Promise<BrandActionResponse> {
    return apiClient.post<BrandActionResponse>(`brands/${brandSlug}/restore`, {});
  },

  /**
   * Permanently delete a brand and all associated data
   * SuperAdmin only - IRREVERSIBLE
   */
  async deleteBrand(brandSlug: string): Promise<BrandActionResponse> {
    return apiClient.delete<BrandActionResponse>(`brands/${brandSlug}`);
  }
};

// Named exports for direct imports
export const {
  addRssFeed,
  deleteRssFeed,
  listRssFeeds,
  validateFeed,
  refreshFeed,
  getUserBrands,
  getUserBrandsById,
  getAllUsers,
  getBrand,
  getBrandUsers,
  addUserToBrand,
  removeUserFromBrand,
  updateUserRole,
  acceptInvitation,
  toggleAutoRefresh,
  getNewItemsCount,
  getEditorialStats,
  getEditorialInsights,
  getDeletePreview,
  archiveBrand,
  restoreBrand,
  deleteBrand
} = brandApi;

export default brandApi;
